import { Analytics, getAnalytics, isSupported, logEvent, setUserProperties } from 'firebase/analytics';

import { getInstallationsYvIid } from '../api/installations';
import { firebaseApp } from './app';
import { EventTypes } from './types';

let analytics: Analytics | null = null;
let hasSetWebYvIid = false;

const firebaseGetAnalytics = async () => {
  if (!(await isSupported())) {
    return null;
  }
  if (!analytics) {
    analytics = getAnalytics(firebaseApp);
  }
  return analytics;
};

/**
 * Log an analytics event with Firebase. Returns a promise that should be awaited if any subsequent
 * actions have side-effects such as navigation to ensure that event logging is completed before
 * proceeding.
 *
 * @example
 * ```typescriptreact
 * import { useRouter } from 'next/router';
 * import { useEffect } from 'react';
 * import { firebaseLogEvent } from 'utils/firebase';
 *
 * function ExamplePage() {
 *   useEffect(() => void firebaseLogEvent('GivingScreenView', { screen: 'giving_form' }), []);
 *   return <div></div>;
 * }
 *
 * function ExamplePageWithNavigation() {
 *   const router = useRouter();
 *   return (
 *     <button
 *       onClick={async () => {
 *         await firebaseLogEvent('GivingButtonTap', {
 *           label: 'Button Label',
 *           screen: 'giving_form',
 *         });
 *         router.push('/somepage');
 *       }}
 *     >
 *       Button Label
 *     </button>
 *   );
 * }
 *
 * ```
 */
export const firebaseLogEvent = async <T extends keyof EventTypes>(eventName: T, eventParams: EventTypes[T]) => {
  const urlParams = new URLSearchParams(window.location.search);
  // firebase event parameters must be string or number only
  const params: Record<string, string | number> = {
    experience: 'one_screen',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_content: urlParams.get('utm_content') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_source: urlParams.get('utm_source') || '',
    utm_term: urlParams.get('utm_term') || '',
    ...eventParams,
  };

  if (window.FlutterAnalytics) {
    const message = {
      command: 'logEvent',
      name: eventName,
      parameters: params,
    };
    window.FlutterAnalytics.postMessage(JSON.stringify(message));
  } else {
    const analytics = await firebaseGetAnalytics();
    if (!analytics) {
      return;
    }
    if (!hasSetWebYvIid) {
      await setWebYvIid();
    }
    logEvent<string>(analytics, eventName, params);
  }
};

const setWebYvIid = async () => {
  if (window.FlutterAnalytics || hasSetWebYvIid) {
    return;
  }
  const yv_iid = await getInstallationsYvIid();
  const analytics = await firebaseGetAnalytics();
  if (!analytics) {
    return;
  }
  setUserProperties(analytics, { yv_iid });
  hasSetWebYvIid = true;
};
